import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { WorkflowFiles } from './WorkflowFiles';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Link,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Subtitle1,
  Title3,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { comparableAnswer, validateAnswers, isVisible, type RequestDetail } from '@lg/core';
import {
  QUESTIONS,
  SECTIONS,
  sameAnswer,
  templateLedgerFor,
  type Answers,
  type QuestionContext,
  type SectionId,
} from '@lg/shared';
import { useApi } from '../api/client';
import { PeopleWorkspace } from '../people/PeopleWorkspace';
import { DeviationTable } from '../request/DeviationTable';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { STATUS_COLOR, STATUS_LABEL, errorMessage } from '../ui/format';
import { QuestionField } from './QuestionField';
import { config } from '../config';

type StepId = SectionId | 'review';
const STEPS: Array<{ id: StepId; title: string }> = [...SECTIONS, { id: 'review', title: 'Review' }];

const useStyles = makeStyles({
  // minmax(0, 1fr): the form column may shrink below its content's natural width,
  // so wide rows reflow instead of pushing the card off the page.
  layout: {
    display: 'grid',
    gridTemplateColumns: '240px minmax(0, 1fr)',
    gap: tokens.spacingHorizontalXL,
    alignItems: 'start',
    '@media (max-width: 900px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  content: { minWidth: 0 },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    position: 'sticky',
    top: tokens.spacingVerticalL,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    border: '1px solid transparent',
    borderLeft: '4px solid transparent',
    background: 'transparent',
    textAlign: 'left',
    color: tokens.colorNeutralForeground1,
    font: 'inherit',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1,
      borderTopColor: tokens.colorNeutralStroke2,
      borderRightColor: tokens.colorNeutralStroke2,
      borderBottomColor: tokens.colorNeutralStroke2,
    },
  },
  // Current step: Legends skin — blue left rule, filled number circle.
  navActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderLeftColor: tokens.colorBrandStroke1,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    '& > span:first-child': {
      backgroundColor: tokens.colorBrandBackground,
      borderTopColor: tokens.colorBrandBackground,
      borderRightColor: tokens.colorBrandBackground,
      borderBottomColor: tokens.colorBrandBackground,
      borderLeftColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
  navNum: {
    flex: '0 0 26px',
    height: '26px',
    borderRadius: '50%',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  navTitle: { flex: 1 },
  head: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
  card: {
    padding: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: 0,
    // Fallback only: fields are sized to fit, but anything still too wide scrolls
    // with a visible bar instead of being clipped.
    overflowX: 'auto',
  },
  footer: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    marginTop: tokens.spacingVerticalL,
  },
  spacer: { flex: 1 },
  checklist: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    rowGap: tokens.spacingVerticalS,
    columnGap: tokens.spacingHorizontalL,
  },
});

export function WizardPage() {
  const { id = '', section = 'venue' } = useParams();
  const step = (STEPS.some((s) => s.id === section) ? section : 'venue') as StepId;
  const styles = useStyles();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const detail = useQuery({ queryKey: ['request', id], queryFn: () => api.getRequest(id) });

  const [draft, setDraft] = useState<Answers>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const loadedVersion = useRef<string>('');
  useEffect(() => {
    if (!detail.data) return;
    const key = `${detail.data.request.id}:${detail.data.request.version}`;
    if (loadedVersion.current !== key) {
      loadedVersion.current = key;
      setDraft(detail.data.answers);
      setDirty(new Set());
    }
  }, [detail.data]);

  const ctx: QuestionContext | null = useMemo(
    () =>
      detail.data
        ? {
            today: detail.data.today,
            goLiveLeadDays: detail.data.goLiveLeadDays,
            template: detail.data.template,
            defaults: detail.data.defaults,
            activeTemplates: [{ id: detail.data.template.id, name: detail.data.template.id }],
            ledger: templateLedgerFor(config.envProfile.templateCompany),
          }
        : null,
    [detail.data],
  );

  const save = useMutation({
    mutationFn: async (): Promise<RequestDetail | null> => {
      if (dirty.size === 0) return null;
      const patch = Object.fromEntries([...dirty].map((k) => [k, draft[k]]));
      return api.saveAnswers(id, patch);
    },
    onSuccess: (d) => {
      if (!d) return;
      qc.setQueryData(['request', id], d);
      void qc.invalidateQueries({ queryKey: ['requests'] });
      void qc.invalidateQueries({ queryKey: ['packages', id] });
    },
  });

  // Live legal entity ID check (debounced 400 ms, spec 15.2).
  const entityId = typeof draft['entity.id'] === 'string' ? draft['entity.id'] : '';
  const [debouncedId, setDebouncedId] = useState(entityId);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedId(entityId), 400);
    return () => clearTimeout(t);
  }, [entityId]);
  const idCheck = useQuery({
    queryKey: ['idCheck', id, debouncedId],
    queryFn: () => api.checkLegalEntityId(debouncedId, id),
    enabled: debouncedId.length > 0,
  });

  if (detail.isPending || !ctx) return <Spinner label="Loading request…" />;
  if (detail.isError) return <MessageBar intent="error">{errorMessage(detail.error)}</MessageBar>;
  const d = detail.data;
  const editable = d.canEdit;
  // Form checks, plus the live D365 lookup: an ID that already exists fails the step like any other error.
  const formErrors = validateAnswers(draft, ctx);
  const idTaken =
    !formErrors['entity.id'] && debouncedId === entityId && idCheck.data && !idCheck.data.available
      ? (idCheck.data.reason ?? `${entityId} is not available`)
      : null;
  const liveErrors: Record<string, string[]> = idTaken
    ? { ...formErrors, 'entity.id': [...(formErrors['entity.id'] ?? []), idTaken] }
    : formErrors;

  const go = async (target: StepId) => {
    if (editable) await save.mutateAsync();
    navigate(`/requests/${id}/edit/${target}`);
  };
  const index = STEPS.findIndex((s) => s.id === step);
  const next = STEPS[index + 1];
  const prev = STEPS[index - 1];

  const sectionErrorCount = (s: StepId): number => {
    if (s === 'review') return 0;
    if (s === 'people') return d.peopleImport?.status === 'Valid' ? 0 : 1;
    return QUESTIONS.filter((q) => q.section === s && liveErrors[q.key]).length;
  };

  const onChange = (key: string, value: unknown) => {
    setDraft((prevDraft) => ({ ...prevDraft, [key]: value }));
    setDirty((prevDirty) => new Set(prevDirty).add(key));
  };

  const questions = QUESTIONS.filter((q) => q.section === step && isVisible(q, draft, ctx));

  return (
    <div>
      <div className={styles.head}>
        <Title3 as="h1">{d.request.venueName}</Title3>
        <Badge appearance="tint" color={STATUS_COLOR[d.request.status]}>
          {STATUS_LABEL[d.request.status]}
        </Badge>
        <Caption1>Version {d.request.version}</Caption1>
        <span className={styles.spacer} />
        <Link
          as="button"
          onClick={() => {
            const open = () => navigate(`/requests/${id}`);
            if (editable) save.mutateAsync().then(open, open);
            else open();
          }}
        >
          Open request
        </Link>
        <Button
          onClick={() => {
            // Keep what was typed: save the draft, then back to the request list.
            const home = () => navigate('/');
            if (editable) save.mutateAsync().then(home, home);
            else home();
          }}
        >
          {editable ? 'Save and exit' : 'Exit'}
        </Button>
      </div>
      {!editable && (
        <MessageBar intent="info" style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>
            Read only: only the requester can edit, and only before provisioning.
          </MessageBarBody>
        </MessageBar>
      )}
      {editable && d.request.status !== 'Draft' && (
        <MessageBar intent="warning" style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>
            Editing returns this request to Draft and existing approvals will no longer count.
          </MessageBarBody>
        </MessageBar>
      )}
      <div className={styles.layout}>
        <nav className={styles.nav} aria-label="Intake steps">
          {STEPS.map((s, i) => {
            const n = sectionErrorCount(s.id);
            return (
              <button
                key={s.id}
                type="button"
                className={mergeClasses(styles.navItem, s.id === step && styles.navActive)}
                onClick={() => void go(s.id)}
              >
                <span className={styles.navNum}>{i + 1}</span>
                <span className={styles.navTitle}>{s.title}</span>
                {s.id !== 'review' &&
                  (n ? (
                    <Badge size="small" color="danger">
                      {n}
                    </Badge>
                  ) : (
                    <Badge size="small" color="success" appearance="tint">
                      ok
                    </Badge>
                  ))}
              </button>
            );
          })}
        </nav>

        <div className={styles.content}>
          {step === 'people' ? (
            <PeopleWorkspace requestId={id} legalEntityId={d.request.legalEntityId} editable={editable} />
          ) : step === 'review' ? (
            <ReviewStep detail={d} liveErrors={liveErrors} onGo={(s) => void go(s)} />
          ) : (
            <Card className={styles.card}>
              <Subtitle1 as="h2">{STEPS[index]?.title}</Subtitle1>
              {questions.map((q) => (
                <QuestionField
                  key={q.key}
                  question={q}
                  value={draft[q.key]}
                  onChange={(v) => onChange(q.key, v)}
                  errors={dirty.has(q.key) || d.request.version > 1 ? (formErrors[q.key] ?? []) : []}
                  deviation={
                    d.deviations.includes(q.key) || (dirty.has(q.key) && isDeviation(q.key, draft, ctx))
                  }
                  ctx={ctx}
                  disabled={!editable}
                  status={
                    q.key === 'entity.id' && entityId && !formErrors['entity.id']
                      ? idCheck.isFetching || debouncedId !== entityId
                        ? { state: 'checking', text: '' }
                        : idCheck.data
                          ? idCheck.data.available
                            ? { state: 'ok', text: `${entityId} is available` }
                            : { state: 'error', text: idCheck.data.reason ?? 'Not available' }
                          : null
                      : null
                  }
                />
              ))}
            </Card>
          )}
          {step === 'workflows' && (
            <WorkflowFiles
              requestId={id}
              legalEntityId={d.request.legalEntityId}
              template={d.template}
              selected={
                Array.isArray(draft['workflows.selected']) ? draft['workflows.selected'].map(String) : []
              }
              beforeDownload={() => (editable && dirty.size > 0 ? save.mutateAsync() : Promise.resolve())}
            />
          )}

          {save.isError && <MessageBar intent="error">{errorMessage(save.error)}</MessageBar>}
          <div className={styles.footer}>
            {prev && <Button onClick={() => void go(prev.id)}>Back</Button>}
            <span className={styles.spacer} />
            {save.isPending && <Spinner size="tiny" label="Saving…" />}
            {editable && dirty.size > 0 && <Button onClick={() => save.mutate()}>Save draft</Button>}
            {next && (
              <Button appearance="primary" onClick={() => void go(next.id)}>
                Save and continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function isDeviation(key: string, a: Answers, ctx: QuestionContext): boolean {
  const q = QUESTIONS.find((x) => x.key === key);
  if (!q?.defaultValue || key === 'finance.templateId') return false;
  const v = a[key];
  return v !== undefined && !sameAnswer(q.defaultValue(ctx), comparableAnswer(q, v));
}

function ReviewStep({
  detail,
  liveErrors,
  onGo,
}: {
  detail: RequestDetail;
  liveErrors: Record<string, string[]>;
  onGo: (s: StepId) => void;
}) {
  const styles = useStyles();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const people = detail.peopleImport;
  const rows = SECTIONS.map((s) => {
    if (s.id === 'people') {
      const ok = people?.status === 'Valid';
      return {
        s,
        ok,
        text: people
          ? `${people.status} · ${people.rowCount} rows · ${people.errorCount} errors · ${people.warningCount} warnings`
          : 'Not loaded yet',
      };
    }
    const n = QUESTIONS.filter((q) => q.section === s.id && liveErrors[q.key]).length;
    return { s, ok: n === 0, text: n === 0 ? 'Complete' : `${n} question${n > 1 ? 's' : ''} to fix` };
  });
  const ready = rows.every((r) => r.ok);
  return (
    <Card className={styles.card}>
      <Subtitle1 as="h2">Review</Subtitle1>
      <Body1>
        Gate 1 runs these checks automatically when you submit, and again at the final confirmation.
      </Body1>
      <div className={styles.checklist}>
        {rows.map(({ s, ok, text }) => (
          <div key={s.id} style={{ display: 'contents' }}>
            <Link as="button" onClick={() => onGo(s.id)}>
              {s.title}
            </Link>
            <Badge appearance="tint" color={ok ? 'success' : 'danger'}>
              {text}
            </Badge>
          </div>
        ))}
      </div>
      {detail.deviationDetails.length > 0 && (
        <>
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>Differs from the template</MessageBarTitle>
              The finance approver sees this comparison at gate 2. Everything else comes from the template as
              is.
            </MessageBarBody>
          </MessageBar>
          <DeviationTable details={detail.deviationDetails} />
        </>
      )}
      <MessageBar intent={ready ? 'success' : 'info'} layout="multiline">
        <MessageBarBody>
          {ready ? 'Everything gate 1 checks is in place.' : 'Fix the items above before submitting.'}{' '}
          <RouterLink to={`/requests/${detail.request.id}`}>Open the request</RouterLink> to see the data
          package it will produce.
        </MessageBarBody>
      </MessageBar>
      {detail.canEdit && detail.request.status === 'Draft' && (
        <div>
          <ConfirmDialog
            trigger={
              <Button appearance="primary" disabled={!ready || !detail.gate1.passed}>
                Submit for approval
              </Button>
            }
            title="Submit for approval"
            confirmLabel="Submit"
            onConfirm={async () => {
              qc.setQueryData(['request', detail.request.id], await api.submitRequest(detail.request.id));
              void qc.invalidateQueries({ queryKey: ['requests'] });
              navigate(`/requests/${detail.request.id}`);
            }}
          >
            <Body1>
              Gate 1 runs again now. If it passes, the request goes to the finance controller (gate 2), then
              the D365 administrator (gate 3). Editing afterwards sends it back to Draft.
            </Body1>
          </ConfirmDialog>
          {ready && !detail.gate1.passed && <Caption1> {detail.gate1.reasons.join('; ')}</Caption1>}
        </div>
      )}
    </Card>
  );
}
